import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { ReactNode } from 'react';

import { Badge } from '../../../../primitives/badge';
import { Button } from '../../../../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../primitives/popover';
import { CONVERT_UNIT, convertBytes } from '../../../../utils/convert-bytes';
import { LabelWithoutRef } from '../../../label/label-without-ref';
import { FileData } from '../utils/get-files-data';
import { getSeverity, SEVERITY } from '../utils/get-severity';

const POPOVER_WARNING_DETAIL = (
  <>
    <div className="space-y-2">
      <p className="font-semibold">Posibles consecuencias</p>
      <ul className="space-y-2 text-xs">
        <li>
          <p>Rendimiento reducido</p>
          <p className="text-muted-foreground">Carga lenta de la página</p>
          <p className="text-muted-foreground">Mayor tasa de abandono</p>
        </li>

        <li>
          <p>Experiencia degradada</p>
          <p className="text-muted-foreground">Interrupciones o errores de visualización</p>
        </li>
      </ul>
    </div>
    <div className="space-y-2">
      <p className="font-semibold">Recomendación</p>
      <p className="text-xs text-muted-foreground">
        <span className="text-primary">Optimiza los archivos antes de subirlos</span> para
        garantizar un rendimiento óptimo
      </p>
    </div>
  </>
);

interface PreviewDetailProps {
  acceptedFiles: FileData[];
  disabled?: boolean;
  popoverWarningDetail?: ReactNode;
  sizeAlert?: {
    none: number;
    medium: number;
  };
}

export function PreviewDetail({
  acceptedFiles,
  disabled = false,
  sizeAlert,
  popoverWarningDetail,
}: PreviewDetailProps) {
  const acceptedFilesLength = acceptedFiles.length;
  const { medium, high, total } = getNumberOfFilesBySeverity({
    acceptedFiles,
    sizeAlert,
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <LabelWithoutRef>
        {acceptedFilesLength} archivo{acceptedFilesLength === 1 ? '' : 's'}
      </LabelWithoutRef>
      {total === 0 ? null : (
        <Popover>
          <PopoverTrigger asChild disabled={disabled}>
            <Button className="text-warning" size="sm" variant="link">
              <ExclamationTriangleIcon className="size-4" />
              <span>Advertencia</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="grid w-[22rem] gap-4 text-sm">
            <p className="text-base">
              Archivo{total === 1 ? '' : 's'} de gran tamaño detectado
              {total === 1 ? '' : 's'}
            </p>

            <div className="space-y-2">
              <p className="font-semibold">Estás a punto de subir</p>
              <ul className="space-y-2 rounded-lg bg-accent p-4 text-accent-foreground">
                {high > 0 ? (
                  <li>
                    {high} archivo{high === 1 ? '' : 's'} con tamaño{' '}
                    <Badge className="h-5" variant="destructive">
                      crítico
                    </Badge>{' '}
                    +
                    {convertBytes({
                      bytes: sizeAlert?.medium ?? 0,
                      toFixed: 0,
                      to: CONVERT_UNIT.megabyte,
                    })}
                  </li>
                ) : null}
                {medium > 0 ? (
                  <li>
                    {medium} archivo{medium === 1 ? '' : 's'} con tamaño{' '}
                    <Badge className="h-5" variant="warning">
                      subóptimo
                    </Badge>{' '}
                    +
                    {convertBytes({
                      bytes: sizeAlert?.none ?? 0,
                      toFixed: 0,
                      to: CONVERT_UNIT.megabyte,
                    })}
                  </li>
                ) : null}
              </ul>
            </div>

            {popoverWarningDetail ?? POPOVER_WARNING_DETAIL}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function getNumberOfFilesBySeverity({ acceptedFiles, sizeAlert }: PreviewDetailProps) {
  let medium = 0;
  let high = 0;

  acceptedFiles.forEach(({ file }) => {
    const { severity } = getSeverity({ fileSize: file.size, sizeAlert });
    if (severity === SEVERITY.medium) {
      medium += 1;
    } else if (severity === SEVERITY.high) {
      high += 1;
    }
  });

  return {
    [SEVERITY.medium]: medium,
    [SEVERITY.high]: high,
    total: medium + high,
  };
}
